import { motion } from "framer-motion";
import { Zap } from "lucide-react";
import { AppStoreBadge } from "./AppStoreBadge";
import { GooglePlayBadge } from "./GooglePlayBadge";
import { WEB_POS_DEMO_URL } from "@/lib/links";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.6, ease: "easeOut" } }),
};

export const Hero = ({ t, lang }) => (
  <section data-testid="hero-section" className="relative overflow-hidden">
    <div
      className="absolute inset-0 bg-cover bg-center opacity-25"
      style={{ backgroundImage: "url(https://images.pexels.com/photos/29012807/pexels-photo-29012807.jpeg)" }}
    />
    <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-[#050505]/80 to-[#050505]" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-24 pb-28 md:pt-36 md:pb-40">
      <div className="max-w-3xl">
        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}
          className="inline-flex items-center gap-2 bg-orange-500/10 text-orange-400 border border-orange-500/20 px-3 py-1 rounded-full text-xs font-medium mb-8">
          <Zap className="w-3 h-3" fill="currentColor" />
          {t.hero.badge}
        </motion.div>
        <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
          data-testid="hero-title"
          className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-tight text-white">
          {t.hero.title1}{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F7931A] to-[#FFC000]">{t.hero.title2}</span>{" "}
          {t.hero.title3}
        </motion.h1>
        <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2}
          className="mt-6 text-base md:text-lg text-zinc-400 leading-relaxed max-w-2xl">
          {t.hero.subtitle}
        </motion.p>
        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
          <AppStoreBadge t={t} lang={lang} testId="hero-app-store-badge" />
          <GooglePlayBadge t={t} lang={lang} testId="hero-google-play-badge" />
          <a href={WEB_POS_DEMO_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-demo-cta"
            className="inline-flex items-center justify-center gap-2 bg-white/5 text-white border border-white/10 font-semibold px-7 py-3.5 rounded-lg hover:bg-white/10 transition-all">
            {t.hero.ctaSecondary}
          </a>
        </motion.div>
        <motion.p variants={fadeUp} initial="hidden" animate="show" custom={4}
          data-testid="hero-app-store-note" className="mt-4 text-sm text-zinc-500">
          {t.stores.note}
        </motion.p>
        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={5}
          className="mt-16 grid grid-cols-3 gap-6 max-w-lg border-t border-white/10 pt-8">
          {[[t.hero.stat1, t.hero.stat1v], [t.hero.stat2, t.hero.stat2v], [t.hero.stat3, t.hero.stat3v]].map(([label, value]) => (
            <div key={label}>
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">{label}</p>
              <p className="text-sm font-semibold text-white">{value}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  </section>
);
